import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Field, reduxForm, getFormValues } from 'redux-form';
import { debounce } from 'lodash';
import classNames from 'classnames';
import WarningPopup from '../UI/WarningPopup';
import { patientFormTranslatedStrings as messages } from '../../translations/PatientFormTranslations';
import {
  userClickedOnConfirmErrorGeneralPopup,
  userClickedOnCancelPatientForm,
  userClickedOnSubmitPatientForm,
  userChangedInputPatient,
  userCallClearPatientAction,
  userCallSearchPatient,
  saveSuppressClearPatientFieldsDialog,
  setSuppressClearPatientFieldsDialog,
  userConfirmedClearingForm
} from '../../redux/feature/patient-management/patient-management.actions';
import TranslatedContent from '../Translations/TranslatedContent';
import DynamicInputField from '../DynamicInputField/DynamicInputField';
import Alert from '../UI/Alert';
import utils from '../../utils/utils';
import Footer from '../UI/Footer';
import styles from './PatientManagementForm.module.scss';
import { FieldName } from '../../utils/enums';
import { fullDateOfBirthMask } from '../../consts';
import { getDisplayedFormFields, getGenderAtBirthFieldTransformer } from './formFieldOrdering';
import PatientFormHeader from './PatientFormHeader';
import PatientFormFooterActions from './PatientFormFooterActions';
import PatientManagementFormSkeleton from './PatientManagementFormSkeleton';

class PatientForm extends React.Component {
  static propTypes = {
    /**
     * https://redux-form.com/8.2.2/docs/api/props.md/#-code-submitting-boolean-code-
     */
    submitting: PropTypes.bool.isRequired,
    /**
     * https://redux-form.com/8.2.2/docs/api/props.md/#-code-invalid-boolean-code-
     */
    invalid: PropTypes.bool.isRequired,
    /**
     * https://redux-form.com/8.2.2/docs/api/props.md/#-code-invalid-boolean-code-
     */
    patientId: PropTypes.string,
    companyId: PropTypes.string,
    patientType: PropTypes.string,
    config: PropTypes.object,
    initialValues: PropTypes.object,
    formValues: PropTypes.object,
    formFields: PropTypes.array.isRequired,
    conflictedPatient: PropTypes.object,
    patientCancelButtonVariant: PropTypes.string,
    errorConflictAlertVariant: PropTypes.string,
    formHeaderTitleTranslation: PropTypes.object,
    patientCancelButtonTranslation: PropTypes.object,
    patientSubmitButtonTranslation: PropTypes.object,
    patientConfirmButtonTranslation: PropTypes.object,
    errorGeneralMessageTranslation: PropTypes.object,
    isEditPatient: PropTypes.bool.isRequired,
    isReadOnlyForm: PropTypes.bool.isRequired,
    isPatientSubmitDisabled: PropTypes.bool,
    isErrorConflictAlert: PropTypes.bool.isRequired,
    isErrorConflictPopup: PropTypes.bool.isRequired,
    isErrorGeneralPopup: PropTypes.bool.isRequired,
    userClickedOnConfirmErrorGeneralPopup: PropTypes.func.isRequired,
    userClickedOnCancelPatientForm: PropTypes.func.isRequired,
    userClickedOnSubmitPatientForm: PropTypes.func.isRequired,
    userChangedInputPatient: PropTypes.func.isRequired,
    userCallClearPatientAction: PropTypes.func.isRequired,
    userCallSearchPatient: PropTypes.func.isRequired,
    isGeneratedChartNumberRequired: PropTypes.bool,
    shouldHideGenderAndDob: PropTypes.bool,
    isInlineEdit: PropTypes.bool,
    showLoader: PropTypes.bool,
    isIntuitiveFlow: PropTypes.bool,
    isInitialConfirmAllowed: PropTypes.bool,
    isClearDataPopup: PropTypes.bool,
    isClearPatientFieldsDialogSuppressed: PropTypes.bool,
    saveSuppressClearPatientFieldsDialog: PropTypes.func.isRequired,
    setSuppressClearPatientFieldsDialog: PropTypes.func.isRequired,
    userConfirmedClearingForm: PropTypes.func.isRequired,
    initialize: PropTypes.func.isRequired,
    change: PropTypes.func.isRequired,
    untouch: PropTypes.func.isRequired,
    dirty: PropTypes.bool
  };

  componentDidMount() {
    window.addEventListener('touchmove', this.handleScroll);
  }

  componentDidUpdate() {
    this.updateGeneratedChartNumber(this.props.formValues);
  }

  componentWillUnmount() {
    window.removeEventListener('touchmove', this.handleScroll);
  }

  getConflictPatientDetails = (dateOfBirthFormat, regulatoryDateOfBirthMask) => {
    const { conflictedPatient, shouldHideGenderAndDob } = this.props;
    if (!conflictedPatient || !conflictedPatient.fullName) {
      return '';
    }

    const isGender = Number.isInteger(conflictedPatient.gender);
    const dateOfBirth = conflictedPatient.dateOfBirth;
    const chartNumber = conflictedPatient.chartNumber;

    const msg = (
      <>
        {<TranslatedContent contentArray={[conflictedPatient.fullName.trim()]} />}
        {!shouldHideGenderAndDob && isGender && ', '}
        {!shouldHideGenderAndDob && isGender && (
          <TranslatedContent contentArray={[conflictedPatient.gender ? messages.female : messages.male]} />
        )}
        {!shouldHideGenderAndDob && dateOfBirth && ', '}
        {!shouldHideGenderAndDob &&
          dateOfBirth &&
          utils.formatDateOfBirth(dateOfBirth, dateOfBirthFormat, regulatoryDateOfBirthMask)}
        {chartNumber && ', '}
        {chartNumber && <TranslatedContent contentArray={[conflictedPatient.chartNumber]} />}
      </>
    );
    return msg;
  };

  updateGeneratedChartNumber = formValues => {
    if (this.props.isGeneratedChartNumberRequired) {
      const generatedChartNumber =
        (formValues.firstName ? formValues.firstName[0] : '') +
        (formValues.lastName ? formValues.lastName[0] : '') +
        initialChartNumber;
      this.props.change(FieldName.CHART_NUMBER, generatedChartNumber);
    }
  };

  handleChange = debounce((e, isKeyField) => {
    const { formValues, companyId, isEditPatient, formLastInvalid, invalid, userChangedInputPatient } = this.props;
    this.updateGeneratedChartNumber(formValues);
    userChangedInputPatient({ form: formValues, companyId, isEditPatient, isKeyField, formLastInvalid, invalid });
  }, 300);

  forceDateOfBirthChange = value => {
    this.props.change(FieldName.DATE_OF_BIRTH, value);
  };

  handleFormSubmit = e => {
    const { formValues, patientId, companyId, conflictedPatient, userClickedOnSubmitPatientForm } = this.props;
    e.preventDefault();
    userClickedOnSubmitPatientForm({ form: formValues, patientId, companyId, conflictedPatient });
  };

  handleScroll = e => {
    document.activeElement.blur();
  };

  hasFormValues = () => {
    const { formValues } = this.props;
    if (!formValues) {
      return false;
    }

    return Object.values(formValues).some(value => {
      if (value === null || value === undefined) {
        return false;
      }
      if (typeof value === 'string') {
        return value.trim().length > 0;
      }
      return true;
    });
  };

  isClearEnabled = () => this.props.dirty || this.hasFormValues();

  handleClearPatientDataClicked = () => {
    if (!this.isClearEnabled()) {
      return;
    }
    const { userCallClearPatientAction, isClearPatientFieldsDialogSuppressed } = this.props;
    if (isClearPatientFieldsDialogSuppressed) {
      this.clearPatientData();
      return;
    }
    userCallClearPatientAction(true);
  };

  handleSearchPatientClicked = () => {
    const { userCallSearchPatient } = this.props;
    userCallSearchPatient();
  };

  handleConfirmClearPatientData = () => {
    const {
      userCallClearPatientAction,
      isClearPatientFieldsDialogSuppressed,
      saveSuppressClearPatientFieldsDialog
    } = this.props;

    if (isClearPatientFieldsDialogSuppressed) {
      saveSuppressClearPatientFieldsDialog();
    }
    this.clearPatientData();
    userCallClearPatientAction(false);
  };

  handleCancelClearPatientData = () => {
    const { userCallClearPatientAction, setSuppressClearPatientFieldsDialog } = this.props;
    setSuppressClearPatientFieldsDialog(false);
    userCallClearPatientAction(false);
  };

  handleDontShowClearDataPopupCheck = isChecked => {
    const { setSuppressClearPatientFieldsDialog } = this.props;
    setSuppressClearPatientFieldsDialog(isChecked);
  };

  clearPatientData = () => {
    const fullNameField = 'fullName';
    const resetFields = [
      FieldName.CHART_NUMBER,
      FieldName.DATE_OF_BIRTH,
      FieldName.FIRST_NAME,
      FieldName.GENDER,
      FieldName.LAST_NAME,
      fullNameField
    ];

    const { change, untouch, userConfirmedClearingForm } = this.props;
    resetFields.forEach(field => {
      change(field, null);
    });
    // touchOnChange is true, so programmatic `change` marks fields touched; DateInput only syncs
    // `value` into local state when !touched — untouch after clear so DoB (masked input) empties.
    resetFields.forEach(field => {
      untouch(field);
    });
    userConfirmedClearingForm();
  };

  render() {
    const {
      submitting,
      invalid,
      formFields,
      conflictedPatient,
      patientCancelButtonVariant,
      errorConflictAlertVariant,
      isEditPatient,
      isReadOnlyForm,
      isPatientSubmitDisabled,
      isErrorConflictAlert,
      isErrorGeneralPopup,
      formHeaderTitleTranslation,
      patientCancelButtonTranslation,
      patientSubmitButtonTranslation,
      patientConfirmButtonTranslation,
      errorGeneralMessageTranslation,
      userClickedOnCancelPatientForm,
      userClickedOnConfirmErrorGeneralPopup,
      dateOfBirthFormat,
      regulatoryDateOfBirthMask,
      shouldHideGenderAndDob,
      isInlineEdit,
      isIntuitiveFlow,
      isClearDataPopup,
      dirty,
      isInitialConfirmAllowed
    } = this.props;

    const hasFormValues = this.hasFormValues();
    const isSubmitDisabled =
      invalid ||
      submitting ||
      isPatientSubmitDisabled ||
      !hasFormValues ||
      (isInlineEdit && !dirty && !isInitialConfirmAllowed);
    const shouldShowSkeleton = this.props.showLoader && isInlineEdit;

    if (shouldShowSkeleton) {
      return <PatientManagementFormSkeleton />;
    }

    const footerActions = (
      <PatientFormFooterActions
        isInlineEdit={isInlineEdit}
        isReadOnlyForm={isReadOnlyForm}
        patientCancelButtonVariant={patientCancelButtonVariant}
        onCancel={userClickedOnCancelPatientForm}
        patientCancelButtonTranslation={patientCancelButtonTranslation}
        isSubmitDisabled={isSubmitDisabled}
        cancelButtonClassName={styles.cancelButton}
        submitButtonClassName={styles.submitButton}
        patientConfirmButtonTranslation={patientConfirmButtonTranslation}
        patientSubmitButtonTranslation={patientSubmitButtonTranslation}
      />
    );

    const formContainerClassName = classNames(styles.formContainer, {
      [styles.formContainerDisplayColumn]: shouldHideGenderAndDob
    });
    const displayedFormFields = getDisplayedFormFields(formFields, {
      isIntuitiveFlow: isIntuitiveFlow && isInlineEdit,
      transformField: getGenderAtBirthFieldTransformer(messages.genderAtBirth)
    });

    return (
      <>
        <form className={classNames({ [styles.intuitive]: isIntuitiveFlow && isInlineEdit })} onSubmit={this.handleFormSubmit} onScroll={this.handleScroll}>
          <PatientFormHeader
            isInlineEdit={isInlineEdit}
            isIntuitiveFlow={isIntuitiveFlow}
            onClear={this.handleClearPatientDataClicked}
            onSearch={this.handleSearchPatientClicked}
            isClearEnabled={this.isClearEnabled()}
            formHeaderTitleTranslation={formHeaderTitleTranslation}
            isRxTakenForScan={this.props.isRxTakenForScan}
          />
          <div className={formContainerClassName}>
            {displayedFormFields.map(field => {
              const fieldProps = {
                conflictedPatient,
                readOnly: isReadOnlyForm || field.isDisabled,
                canShowErrorOnLoad: isEditPatient,
                maxLength: field.name === FieldName.LAST_NAME ? this.props.maxPatientLastNameLength : '',
                format: field.name === FieldName.DATE_OF_BIRTH ? dateOfBirthFormat : '',
                isIntuitiveFlow: isIntuitiveFlow && isInlineEdit
              };

              const shouldHide =
                shouldHideGenderAndDob && (field.name === FieldName.DATE_OF_BIRTH || field.name === FieldName.GENDER);

              return (
                field.isVisible &&
                !shouldHide && (
                  <div className={styles.inputFieldColumn} key={field.name}>
                    <Field
                      key={field.name}
                      name={field.name}
                      component={DynamicInputField}
                      validate={field.validations}
                      formFields={field}
                      props={fieldProps}
                      onChange={e => this.handleChange(e, field.isKeyField)}
                      forceDateOfBirthChange={this.forceDateOfBirthChange}
                      normalize={field.normalize}
                    />
                  </div>
                )
              );
            })}
          </div>

          <div className={styles.alert}>
            {isErrorConflictAlert && conflictedPatient && !isReadOnlyForm && (
              <Alert
                variant={errorConflictAlertVariant}
                heading={<TranslatedContent contentArray={[messages.conflictHeader]} />}
                content={this.getConflictPatientDetails(dateOfBirthFormat, regulatoryDateOfBirthMask)}
              />
            )}
          </div>

          <Footer className={styles.footer} actions={footerActions} />
        </form>
        <WarningPopup
          open={!!isErrorGeneralPopup}
          handleConfirmClick={userClickedOnConfirmErrorGeneralPopup}
          confirmText={<TranslatedContent contentArray={[messages.buttonOk]} />}
          title={<TranslatedContent contentArray={[messages.errorGeneralHeader]} />}
          message={<TranslatedContent contentArray={[errorGeneralMessageTranslation]} />}
        />
        <WarningPopup
          open={!!isClearDataPopup}
          handleConfirmClick={this.handleConfirmClearPatientData}
          handleCancelClick={this.handleCancelClearPatientData}
          handleCheckboxChange={this.handleDontShowClearDataPopupCheck}
          confirmText={<TranslatedContent contentArray={[messages.buttonOk]} />}
          cancelText={<TranslatedContent contentArray={[messages.buttonCancel]} />}
          checkboxText={<TranslatedContent contentArray={[messages.checkboxDontShowAgain]} />}
          title={<TranslatedContent contentArray={[messages.confirmation]} />}
          message={<TranslatedContent contentArray={[messages.clearPatientDataConfirmation]} />}
          shouldEmitToHost={true}
        />
      </>
    );
  }
}

const formName = 'PatientForm';

const initialChartNumber = Math.floor((new Date().valueOf() / 1000 / 60) % 1000000).toString();

const mapStateToProps = state => {
  const { patientManagement, shell } = state;
  const maxPatientLastNameLength =
    shell.config.regulatorySettings && shell.config.regulatorySettings.maxPatientLastNameLength
      ? shell.config.regulatorySettings.maxPatientLastNameLength
      : '';

  return {
    patientId: shell.fromUrl.patientId,
    companyId: shell.fromUrl.companyId,
    patientType: shell.fromUrl.patientType,
    config: shell.config,
    initialValues: patientManagement.initialPatientData,
    formValues: getFormValues(formName)(state),
    formFields: patientManagement.formFields,
    conflictedPatient: patientManagement.conflictedPatient,
    patientCancelButtonVariant: patientManagement.patientCancelButtonVariant,
    errorConflictAlertVariant: patientManagement.errorConflictAlertVariant,
    formHeaderTitleTranslation: patientManagement.formHeaderTitleTranslation,
    patientCancelButtonTranslation: patientManagement.patientCancelButtonTranslation,
    patientSubmitButtonTranslation: patientManagement.patientSubmitButtonTranslation,
    patientConfirmButtonTranslation: patientManagement.patientConfirmButtonTranslation,
    errorGeneralMessageTranslation: patientManagement.errorGeneralMessageTranslation,
    formLastInvalid: patientManagement.formLastInvalid,
    isEditPatient: patientManagement.isEditPatient,
    isReadOnlyForm: patientManagement.isReadOnlyForm,
    isPatientSubmitDisabled: patientManagement.isPatientSubmitDisabled,
    isErrorConflictAlert: patientManagement.isErrorConflictAlert,
    isErrorConflictPopup: patientManagement.isErrorConflictPopup,
    isErrorGeneralPopup: patientManagement.isErrorGeneralPopup,
    isGeneratedChartNumberRequired: patientManagement.isGeneratedChartNumberRequired,
    maxPatientLastNameLength,
    isClearDataPopup: patientManagement.isClearDataPopup,
    isClearPatientFieldsDialogSuppressed: patientManagement.isClearPatientFieldsDialogSuppressed,
    dateOfBirthFormat: shell.fromUrl.dateOfBirthFormat
      ? shell.fromUrl.dateOfBirthFormat
      : shell.config.regulatorySettings
      ? shell.config.regulatorySettings.dobMask
      : fullDateOfBirthMask,
    regulatoryDateOfBirthMask: shell.config.regulatorySettings ? shell.config.regulatorySettings.dobMask : undefined,
    shouldHideGenderAndDob: shell.fromUrl.shouldHideGenderAndDob,
    isInlineEdit: !!shell.fromUrl.isInlineEdit,
    isIntuitiveFlow: !!shell.fromUrl.isIntuitiveFlow,
    isInitialConfirmAllowed: !!shell.fromUrl.isInitialConfirmAllowed,
    isRxTakenForScan: !!shell.fromUrl.isRxTakenForScan,
    showLoader: shell.showLoader
  };
};

const mapDispatchToProps = {
  userClickedOnConfirmErrorGeneralPopup,
  userClickedOnCancelPatientForm,
  userClickedOnSubmitPatientForm,
  userCallClearPatientAction,
  userCallSearchPatient,
  userChangedInputPatient,
  saveSuppressClearPatientFieldsDialog,
  setSuppressClearPatientFieldsDialog,
  userConfirmedClearingForm
};

PatientForm = reduxForm({
  form: formName,
  touchOnBlur: true,
  touchOnChange: true,
  enableReinitialize: true
})(PatientForm);

PatientForm = connect(
  mapStateToProps,
  mapDispatchToProps
)(PatientForm);

export default PatientForm;
